import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { sortPointsByClockwise, MatchingColorsMap } from 'components/TextractCanvas/utils';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';

import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { FormControlLabelSwitch } from 'components/mui/Form/FormControl/FormControlLabelSwitch';
import Switch from 'components/mui/Form/Switch/SwitchBase';

import boundIcon from './assets/bound.svg';
import unboundIcon from './assets/unbound.svg';
import CheckIcon from '@mui/icons-material/Check';

export function TextractFieldBinding({
  bindingCell,
  fields,
  isMandatory,
  width,
  height,
  deltaAngle,
  onClick,
  name,
  bound,
  selectedFieldIndex,
  mandatoryField,
  disabledFields,
  onSelect,
  isLoading,
}) {
  const [mandatory, setMandatory] = useState(isMandatory);
  const toggleMandatory = useCallback(() => setMandatory((prevState) => !prevState), [setMandatory]);
  const popupState = usePopupState({ variant: 'popover', popupId: 'TextractFieldsDropdown' });

  const triggerProps = useMemo(() => {
    const menuProps = bindTrigger(popupState);

    return {
      ...menuProps,
      onClick: (...args) => {
        onClick(name);
        menuProps?.onClick(...args);
      },
    };
  }, [popupState, onClick, name]);

  useEffect(() => {
    setMandatory(mandatoryField);
  }, [mandatoryField]);

  const onMandatoryChecked = (event) => {
    // if dropdown option selected, invoke parent component callback, otherwise change local checkbox state
    toggleMandatory();
    if (selectedFieldIndex) {
      onSelect(selectedFieldIndex, event.target.checked);
    }
  };

  const disabledFieldsExceptSelected = useMemo(
    () => disabledFields.filter((field) => field !== selectedFieldIndex),
    [disabledFields, selectedFieldIndex],
  );

  const getPointByAngle = useCallback(
    (points) => {
      switch (deltaAngle) {
        case 90:
          return points[1];
        case 180:
          return points[2];
        case 270:
          return points[3];
        default:
          return points[0];
      }
    },
    [deltaAngle],
  );

  const normalizeTopLeft = useCallback(
    (X, Y, shift = 0) => {
      const h = height * Y;
      const w = width * X;

      switch (deltaAngle) {
        case 90:
          return {
            top: h,
            left: w,
          };
        case 180:
          return {
            top: h,
            left: w - shift,
          };
        case 270:
          return {
            top: h - shift,
            left: w - shift,
          };
        default:
          return {
            top: h - shift,
            left: w,
          };
      }
    },
    [deltaAngle, height, width],
  );

  const buttonStyle = useMemo(() => {
    const elementSize = 16;
    // @to-do make use of enum
    const [binding, icon] = bound ? ['match', boundIcon] : ['partial_match', unboundIcon];
    const color = MatchingColorsMap[binding].border;
    const points = sortPointsByClockwise(bindingCell);
    const { X, Y } = getPointByAngle(points);
    const { top, left } = normalizeTopLeft(X, Y, elementSize);

    return bindingCell && bindingCell[0]
      ? {
          position: 'absolute',
          top,
          left,
          width: `${elementSize}px`,
          height: `${elementSize}px`,
          border: 0,
          backgroundImage: `url("${icon}")`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: color,
          backgroundSize: elementSize / 2 - 1,
          zIndex: 100,
          cursor: 'pointer',
        }
      : {};
  }, [bindingCell, bound, getPointByAngle, normalizeTopLeft]);

  return bindingCell && bindingCell[0] ? (
    <>
      <button type="button" {...triggerProps} style={buttonStyle} />
      <Menu
        {...bindMenu(popupState)}
        transitionDuration={0}
        slotProps={{ paper: { sx: { maxHeight: 700, width: 300 } }, list: { disablePadding: true } }}
      >
        <Box sx={{ position: 'sticky', top: 0, p: 2, pb: 1, bgcolor: 'background.paper', zIndex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: 18 }}>
            <FormattedMessage id="3WayMatching.bindings.matchTo" defaultMessage="Matching to" />
          </Typography>
          <FormControlLabelSwitch
            checked={mandatory}
            onChange={onMandatoryChecked}
            control={<Switch />}
            label={<FormattedMessage id="3WayMatching.bindings.mandatory" defaultMessage="Mandatory" />}
            labelPlacement="start"
            sx={{ ml: 0, color: 'text.secondary' }}
          />
          <Divider sx={{ mt: 1 }} />
        </Box>
        {isLoading && fields.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List disablePadding>
            {fields.map((option, index) => (
              <ListItem
                key={option.name}
                disablePadding
                {...(index === selectedFieldIndex
                  ? {
                      secondaryAction: isLoading ? <CircularProgress size={16} /> : <CheckIcon color="success" />,
                    }
                  : {})}
              >
                <ListItemButton
                  key={option.displayName}
                  selected={index === selectedFieldIndex && !isLoading}
                  disabled={disabledFieldsExceptSelected.includes(index)}
                  onClick={() => onSelect(index, mandatory)}
                  dense
                >
                  {option.displayName}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Menu>
    </>
  ) : null;
}
